import { useMemo, useState, useCallback } from "react";
import { useWeeklyStudyTimeQuery } from "@/infrastructure/adapters/study-time-tracking/study-time-tracking.query";
import dayjs from "dayjs";

/** Get Monday of the week containing the given date */
const getMondayOfWeek = (date: dayjs.Dayjs): string => {
  const day = date.day();
  const monday = day === 0 ? date.subtract(6, "day") : date.subtract(day - 1, "day");
  return monday.format("YYYY-MM-DD");
};

export const useWeeklyStudyTime = () => {
  const [weekOffset, setWeekOffset] = useState(0);

  const startDate = useMemo(() => {
    const target = dayjs().add(weekOffset, "week");
    return getMondayOfWeek(target);
  }, [weekOffset]);

  const endDate = useMemo(() => {
    return dayjs(startDate).add(6, "day").format("YYYY-MM-DD");
  }, [startDate]);

  const query = useWeeklyStudyTimeQuery(startDate);

  const goNextWeek = useCallback(() => setWeekOffset((prev) => prev + 1), []);
  const goPrevWeek = useCallback(() => setWeekOffset((prev) => prev - 1), []);
  const goCurrentWeek = useCallback(() => setWeekOffset(0), []);

  /** Format week range as "M/D-M/D" */
  const weekRangeLabel = useMemo(() => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    return `${start.format("M/D")}-${end.format("M/D")}`;
  }, [startDate, endDate]);

  const targetPercent = useMemo(() => {
    if (!query.data) return 0;
    const { totalMinutes, targetMinutes } = query.data;
    if (targetMinutes <= 0) return 0;
    return Math.min(Math.round((totalMinutes / targetMinutes) * 100), 100);
  }, [query.data]);

  return {
    data: query.data, // Return data (can be placeholder)
    isLoading: query.isPending && !query.isPlaceholderData, // Initial load
    isUpdating: query.isFetching, // Fetching new data (switching week)
    startDate,
    endDate,
    weekRangeLabel,
    targetPercent,
    isCurrentWeek: weekOffset === 0,
    goNextWeek,
    goPrevWeek,
    goCurrentWeek,
  };
};
